import type {
  HttpToolParam,
  HttpToolParamLocation,
  HttpToolParamType,
  HttpToolParamValueType,
} from '@kanbu/schema/contracts';
import { useLingui, Trans } from '@lingui/react/macro';
import { Button, Input, Label, Select, SelectItem, TextArea } from '@utima/ui';
import { Plus, Trash2 } from 'lucide-react';

const TYPES: HttpToolParamType[] = ['string', 'number', 'integer', 'boolean'];

export interface HttpToolParamRowsProps {
  location: HttpToolParamLocation;
  params: HttpToolParam[];
  onChange: (params: HttpToolParam[]) => void;
}

/**
 * Editor for a list of flat path/query parameters. Each parameter is either
 * `static` (fixed value, applied server-side) or `llm` (model-filled, with an
 * extraction description and optional enum constraint).
 */
export function HttpToolParamRows({
  location,
  params,
  onChange,
}: HttpToolParamRowsProps) {
  const { t } = useLingui();

  const update = (index: number, patch: Partial<HttpToolParam>) =>
    onChange(params.map((p, i) => (i === index ? { ...p, ...patch } : p)));

  const remove = (index: number) =>
    onChange(params.filter((_, i) => i !== index));

  const add = () =>
    onChange([
      ...params,
      {
        name: '',
        location,
        type: 'string',
        valueType: 'llm',
        required: false,
      },
    ]);

  return (
    <div className='space-y-3'>
      {params.map((param, index) => (
        <div
          key={index}
          className='space-y-2 rounded-radius border border-border p-3'
        >
          <div className='flex items-start gap-2'>
            <div className='flex-1 space-y-1.5'>
              <Label>
                <Trans>Identifier</Trans>
              </Label>
              <Input
                value={param.name}
                onChange={e => update(index, { name: e.target.value })}
                placeholder={t`e.g. customer_id`}
              />
            </div>
            <div className='w-32 space-y-1.5'>
              <Label>
                <Trans>Data type</Trans>
              </Label>
              <Select
                value={param.type}
                onValueChange={value =>
                  update(index, { type: value as HttpToolParamType })
                }
              >
                {TYPES.map(type => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </Select>
            </div>
            <div className='w-32 space-y-1.5'>
              <Label>
                <Trans>Value</Trans>
              </Label>
              <Select
                value={param.valueType}
                onValueChange={value =>
                  update(index, {
                    valueType: value as HttpToolParamValueType,
                  })
                }
              >
                <SelectItem value='llm'>{t`LLM`}</SelectItem>
                <SelectItem value='static'>{t`Static`}</SelectItem>
              </Select>
            </div>
            <Button
              size='sm'
              variant='ghost'
              className='mt-6'
              title={t`Remove`}
              onClick={() => remove(index)}
            >
              <Trash2 className='size-4 text-danger' />
            </Button>
          </div>

          {param.valueType === 'static' ? (
            <div className='space-y-1.5'>
              <Label>
                <Trans>Static value</Trans>
              </Label>
              <Input
                value={param.value ?? ''}
                onChange={e => update(index, { value: e.target.value })}
                placeholder={t`Fixed value sent with every request`}
              />
            </div>
          ) : (
            <>
              <label className='flex cursor-pointer items-center gap-2 text-sm'>
                <input
                  type='checkbox'
                  checked={param.required ?? false}
                  onChange={e => update(index, { required: e.target.checked })}
                />
                <Trans>Required</Trans>
              </label>
              <div className='space-y-1.5'>
                <Label>
                  <Trans>Description</Trans>
                </Label>
                <TextArea
                  value={param.description ?? ''}
                  onChange={e => update(index, { description: e.target.value })}
                  rows={2}
                  placeholder={t`How the model should extract this value`}
                />
              </div>
              <div className='space-y-1.5'>
                <Label>
                  <Trans>Allowed values (optional, comma-separated)</Trans>
                </Label>
                <Input
                  value={(param.enumValues ?? []).join(', ')}
                  onChange={e =>
                    update(index, {
                      enumValues: e.target.value
                        .split(',')
                        .map(v => v.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder={t`e.g. low, medium, high`}
                />
              </div>
            </>
          )}
        </div>
      ))}

      <Button size='sm' variant='secondary' onClick={add}>
        <Plus className='size-4' />
        <Trans>Add param</Trans>
      </Button>
    </div>
  );
}
